<template>
    <b-modal
        ref="dialog"
        title-tag="h2"
        title="Close Diagnosis"
        :lazy="true"
        @hidden="close"
        id="modal-diagnosis-dialog"
    >
        <b-form-group label-cols="3" label="Closed:" label-for="close">
            <date-time-picker
                id="closed"
                v-model="closed"
                v-validate
                :placeholder="{
                    date: 'Closed (required)',
                    time: 'Time (required)'
                }"
                :inner-class="{
                    'border-danger':
                        errors.has('closed') || formError.includes('closed')
                }"
                rules="required"
                name="closed"
                container="#modal-diagnosis-dialog"
                format="datetime"
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Closed By:" label-for="closed by">
            <vue-select
                v-model="closedBy"
                label="fullname"
                :placeholder="staffPlaceholder"
                :disabled="isLoadingListStaff"
                :options="staffOptions"
                :class="{
                    'border-danger': formError.includes('closedBy')
                }"
            >
                <template v-slot:open-indicator>
                    <span></span>
                </template>
            </vue-select>
        </b-form-group>

        <b-form-group label-cols="3" label="Notes:" label-for="notes">
            <b-textarea placeholder="Notes (optional)" v-model="notes" />
        </b-form-group>

        <template v-slot:modal-footer>
            <loader v-if="isSaving" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isSaving"
            >
                Cancel
            </b-button>

            <b-button variant="primary" @click="save" v-if="!isSaving">
                Save
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import moment from "moment";
import { DateTime } from "luxon";
import VueSelect from "vue-select";
import Loader from "@/module/commons/loader";
import DateTimePicker from "@/module/commons/date-time-picker.vue";

export default {
    name: "ModalCloseDiagnosis",
    components: {
        VueSelect,
        Loader,
        DateTimePicker
    },
    props: {
        patientId: {
            type: String
        }
    },
    data() {
        return {
            notes: "",
            formError: [],
            closed: null,
            closedBy: null,
            timeFormat: "hh:mm A",
            isLoadingListStaff: true,
            staffOptions: [],
            isSaving: false,
            record: null
        };
    },
    computed: {
        staffPlaceholder() {
            return this.isLoadingListStaff
                ? "Loading..."
                : "Closed by (required)";
        }
    },
    watch: {
        closedBy(value) {
            if (value) {
                this.clearError("closedBy");
            }
        }
    },
    methods: {
        open(record) {
            this.record = record;
            this.notes = record.note;
            this.initDateTime();
            this.fetchStaffList();
            this.$refs.dialog.show();
        },

        close() {
            this.$refs.dialog.hide();
        },
        clearError(key) {
            this.formError = this.formError.filter(err => err !== key);
        },
        async save() {
            try {
                this.formError = [];
                let momentOpened = moment(this.record.date_opened).tz(this._timeZone); // prettier-ignore

                if (!this.closed) {
                    this.formError.push("closed");
                }

                if (!this.closedBy || this.closedBy === "") {
                    this.formError.push("closedBy");
                }

                if (this.closed < momentOpened) {
                    this.formError.push("closed");
                }

                if (this.formError.length > 0) {
                    return;
                }

                let closed_by_id = this.closedBy ? this.closedBy.id : "";
                let closed_by = this.closedBy ? this.closedBy.fullname : "";
                let closedDate = "";
                let closedTime = "";

                if (this.closed) {
                    closedDate = this.closed.toFormat(this._dateFormat);
                    closedTime = this.closed.toFormat("hh:mm a");
                }

                const fd = new FormData();

                fd.append("referrer", "");
                fd.append("diagnosis_id", this.record.id);
                fd.append("patient_id", this.patientId);
                fd.append("description_id", this.record.description_id);
                fd.append("description_type", this.record.description_type);
                fd.append("closed_by_id", closed_by_id);
                fd.append("closed_by", closed_by);
                fd.append("date_closed", closedDate);
                fd.append("time_closed", closedTime);
                fd.append("date_opened", momentOpened.format(this.getDateFormat())); // prettier-ignore
                fd.append("time_opened", momentOpened.format(this.timeFormat));
                fd.append("status", this.record.status);
                fd.append("is_acute", this.record.is_acute);
                fd.append("opened_by_id", this.record.opened_by.id);
                fd.append("soap_id", "");
                fd.append("note", this.notes);

                this.isSaving = true;

                const res = await this.$http.post(
                    `${process.env.CI_APPS}common/diagnosis/saveDiagnosis`,
                    fd
                );

                if (Number(res.data.state) === 0) {
                    this.close();
                    this._message("Diagnosis closed.");
                    this.$emit("onsuccess");
                } else {
                    throw new Error(res.data.message);
                }
            } catch (e) {
                this.isSaving = false;
                this._message(e.message, "danger");
            }
        },
        initDateTime() {
            this.closed = DateTime.local().setZone(this._timeZone);
        },
        async fetchStaffList(isSetDefault = true) {
            try {
                this.isLoadingListStaff = true;
                const res = await this.$http.get(
                    `${process.env.BARRAMUNDI}actor/staff`
                );

                const response = res.data.response;

                if (
                    response &&
                    response.message &&
                    response.message.code === 200
                ) {
                    this.staffOptions = response.resources || [];
                    this.isLoadingListStaff = false;

                    if (isSetDefault) {
                        this.closedBy = this.$root.currentActor;
                    }
                } else {
                    throw new Error("Unable to get staff list");
                }
            } catch (e) {
                this.isLoadingListStaff = false;
                this.staffOptions = [];
            }
        }
    }
};
</script>
